// biome-ignore-all lint: a

import { createHash } from "crypto";
import type Mdast from "mdast";
import { Extension, fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { mathFromMarkdown } from "mdast-util-math";
import { mdxFromMarkdown } from "mdast-util-mdx";
import { gfm } from "micromark-extension-gfm";
import { math } from "micromark-extension-math";
import { mdxjs } from "micromark-extension-mdxjs";
import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Without } from "@/shared/types";

// Add an `id` key to each node in the Mdast tree type
type WithId<T> = T extends infer U
  ? U extends { children: readonly unknown[] }
    ? Without<U, "children"> & { id: number; children: WithId<U["children"][number]>[] }
    : U & { id: number }
  : never;
type Node = WithId<Mdast.RootContent>;
type Root = Without<Mdast.Root, "children"> & { children: Node[] };

// An mdast extension augmenting each node with a unique id
// This id is used as React key for proper memoization
const idExtension: Extension = {
  transforms: [
    (tree: Mdast.Root): void => {
      const traverse = (node: Node, id: number): void => {
        node.id = id;
        let counter = 0;
        if ("children" in node && Array.isArray(node.children)) {
          for (const child of node.children) traverse(child, counter++);
        }
      };
      traverse(tree as unknown as Node, 0);
    },
  ],
};

const markdownToTree = (content: string) => {
  return fromMarkdown(content, "utf-8", {
    extensions: [gfm(), math(), mdxjs()],
    mdastExtensions: [gfmFromMarkdown(), mathFromMarkdown(), mdxFromMarkdown(), idExtension],
  }) as Root;
};

export const repairMarkdown = (content: string) => {
  // Repair non-closed bold markdown syntax (**)
  const boldMatches = content.match(/\*\*/g);
  if (boldMatches && boldMatches.length % 2 !== 0) {
    return content + "**";
  }
  return content;
};

interface RenderMarkdownComponents {
  parent: (args: { children: React.ReactNode }) => React.ReactNode;
  heading: (args: { children: React.ReactNode; level: number }) => React.ReactNode;
  paragraph: (args: { children: React.ReactNode }) => React.ReactNode;
  bold: (args: { children: React.ReactNode }) => React.ReactNode;
  italic: (args: { children: React.ReactNode }) => React.ReactNode;
  strikethrough: (args: { children: React.ReactNode }) => React.ReactNode;
  link: (args: { children: React.ReactNode; url: string }) => React.ReactNode;
  image: (args: { children: React.ReactNode; url: string; alt?: string | null }) => React.ReactNode;
  list: (args: { children: React.ReactNode; ordered: boolean }) => React.ReactNode;
  listItem: (args: { children: React.ReactNode }) => React.ReactNode;
  table: (args: { children: React.ReactNode }) => React.ReactNode;
  tableRow: (args: { children: React.ReactNode }) => React.ReactNode;
  tableCell: (args: { children: React.ReactNode }) => React.ReactNode;
}

const lastRenders = new Map<
  string,
  {
    hash: string;
    content: string;
    deltaContent: string;
    tree: Root;
    render: React.ReactNode;
    nodesRender: React.ReactNode[];
  }
>();

const repairTree = (parent: Node | Root) => {
  // Return early if the parent has no children or can't contain Markdown
  const hasChildren = "children" in parent && parent.children?.length;
  const canContainMarkdown = !["code", "inlineCode", "math"].includes(parent.type);
  if (!hasChildren || !canContainMarkdown) return;

  for (let nodeIndex = 0; nodeIndex < parent.children.length; nodeIndex++) {
    const node = parent.children.at(nodeIndex)!;

    // Process text nodes to find and recover partial or broken Markdown sequences
    if (node.type === "text") {
      const text = node.value;
      const repaired = repairMarkdown(text);

      // If the node had partial or broken Markdown
      if (repaired !== text) {
        // Replace the original tree node, by a new tree with the repaired Markdown
        const repairedTree = markdownToTree(repaired);
        const newChildren = repairedTree.children.flatMap((child) =>
          child.type === "paragraph" ? child.children : [child],
        );
        parent.children = [
          ...parent.children.slice(0, nodeIndex),
          ...newChildren,
          ...parent.children.slice(nodeIndex + 1),
        ];

        // Adjust nodeIndex to account for the replacement, to skip the new nodes
        nodeIndex += repairedTree.children.length - 1;
      }
    }

    // Repair children recursively
    repairTree(node);
  }
};

const renderTreeNodes = (parent: Node | Root, components: RenderMarkdownComponents) => {
  const renderedNodes: React.ReactNode[] = [];
  const parentChildren = "children" in parent ? parent.children : [];
  const c = components;
  for (const node of parentChildren) {
    let r: React.ReactNode = null;
    const children = renderTreeNodes(node, components);
    if (node.type === "text") {
      const words = node.value.split(" ");
      r = words.flatMap((word, i) => [word, words.length - 1 === i ? "" : " "]);
    } else if (node.type === "heading") r = c.heading?.({ children, level: node.depth });
    else if (node.type === "paragraph") r = c.paragraph?.({ children });
    else if (node.type === "strong") r = c.bold?.({ children });
    else if (node.type === "emphasis") r = c.italic?.({ children });
    else if (node.type === "link") r = c.link?.({ children, url: node.url });
    else if (node.type === "delete") r = c.strikethrough?.({ children });
    else if (node.type === "image") r = c.image?.({ children, url: node.url, alt: node.alt });
    else if (node.type === "list") r = c.list?.({ children, ordered: node.ordered ?? false });
    else if (node.type === "listItem") r = c.listItem?.({ children });
    else if (node.type === "table") r = c.table?.({ children });
    else if (node.type === "tableRow") r = c.tableRow?.({ children });
    else if (node.type === "tableCell") r = c.tableCell?.({ children });
    if (r !== null) renderedNodes.push(<Fragment key={node.id}>{r}</Fragment>);
  }
  return renderedNodes;
};

function renderMarkdown(params: {
  documentKey: string;
  content: string;
  components: RenderMarkdownComponents;
}) {
  let { documentKey, content, components } = params;

  // Trim and normalize the content
  content = content.trim().replace(/\r\n?/g, "\n");
  if (!content.length) return null;

  // Return previous render if the content hasn't changed
  const hash = createHash("md5").update(content).digest("hex");
  const prevRender = lastRenders.get(documentKey);
  if (hash === prevRender?.hash) return prevRender.render;

  // Obtain the delta content since previous render
  let deltaContent = content;
  const hasPrevNodes =
    prevRender?.content.length &&
    prevRender.tree.children.length > 1 &&
    content.startsWith(prevRender.content);
  if (hasPrevNodes) {
    // - Exclude already rendered content
    deltaContent = content.slice(prevRender.content.length);
    // - Re-introduce previous render last node for context
    const lastNodeOffset = prevRender.tree.children.at(-1)?.position?.start.offset;
    if (lastNodeOffset !== undefined) {
      deltaContent = prevRender.deltaContent.slice(lastNodeOffset) + deltaContent;
    }
  }

  // Compute the delta tree
  const deltaTree = markdownToTree(deltaContent);

  // Repair partial or broken Markdown sequences in the delta tree
  repairTree(deltaTree);

  // Fix ids in the delta tree
  let counter = hasPrevNodes ? (prevRender?.tree.children.at(-1)?.id ?? 0) : 0;
  for (const node of deltaTree.children) node.id = counter++;

  // Render the delta tree to React nodes
  const deltaNodesRender = renderTreeNodes(deltaTree, components);

  // Obtain the final nodes render by combining the previous and delta nodes render
  const prevNodesRender = hasPrevNodes ? (prevRender?.nodesRender.slice(0, -1) ?? []) : [];
  const nodesRender = [...prevNodesRender, ...deltaNodesRender];
  const render = components.parent?.({ children: nodesRender });

  // Obtain the full tree by combining the previous and delta tree
  const prevChildren = hasPrevNodes ? (prevRender?.tree.children.slice(0, -1) ?? []) : [];
  const tree: Root = { type: "root", children: [...prevChildren, ...deltaTree.children] };
  console.log(tree);

  // Cache the result for next re-render
  lastRenders.set(documentKey, {
    hash,
    tree,
    content,
    deltaContent,
    render,
    nodesRender,
  });

  // Return the render
  return render;
}

const defaultComponents = {
  parent: ({ children }) => <div>{children}</div>,
  heading: ({ children, level }) => <h1>{children}</h1>,
  paragraph: ({ children }) => <p>{children}</p>,
  bold: ({ children }) => <strong>{children}</strong>,
  italic: ({ children }) => <em>{children}</em>,
  link: ({ children, url }) => <a href={url}>{children}</a>,
  strikethrough: ({ children }) => <s>{children}</s>,
  image: ({ children, url, alt }) => (
    <img alt={alt ?? undefined} src={url}>
      {children}
    </img>
  ),
  list: ({ children, ordered }) => (
    <ul className={ordered ? "list-decimal" : "list-disc"}>{children}</ul>
  ),
  listItem: ({ children }) => <li>{children}</li>,
  table: ({ children }) => <table>{children}</table>,
  tableRow: ({ children }) => <tr>{children}</tr>,
  tableCell: ({ children }) => <td>{children}</td>,
} as RenderMarkdownComponents;

type MarkdownProps = {
  documentKey: string;
  children: string;
  components?: Partial<RenderMarkdownComponents>;
  throttleMs?: number;
};

export const Markdown: FC<MarkdownProps> = ({
  documentKey,
  children,
  components,
  throttleMs = 100,
}) => {
  // Prepare the final components to use for rendering
  const mergedComponents = useMemo(
    () => ({ ...defaultComponents, ...(components ?? {}) }),
    [components],
  );

  // Throttle content updates
  const [throttledContent, setThrottledContent] = useState(children);
  const lastUpdateRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  useEffect(() => {
    if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);

    const now = Date.now();
    const elapsed = now - lastUpdateRef.current;

    if (elapsed >= throttleMs) {
      setThrottledContent(children);
      lastUpdateRef.current = now;
    } else {
      timeoutRef.current = setTimeout(() => {
        setThrottledContent(children);
        lastUpdateRef.current = Date.now();
        timeoutRef.current = undefined;
      }, throttleMs - elapsed);
    }
    return () => {
      if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);
    };
  }, [children, throttleMs]);

  // Render the Markdown content
  const rendered = useMemo(
    () => renderMarkdown({ documentKey, content: throttledContent, components: mergedComponents }),
    [documentKey, throttledContent, mergedComponents],
  );

  return rendered;
};
